import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Switch,
  message,
  Tag,
  Space,
  Popconfirm,
  Alert,
  Avatar,
} from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { connect } from "react-redux";
import { getUserInfo } from "../../utils/redux/reducers/User";
import { ApiManager, Locale } from "../../utils";
import UserSelector from "../../components/main/UserSelector";

const api = ApiManager.getInstance();
const i18n = Locale.getInstance();

function SpecialShareParties({ user }) {
  const canManage = !!user?.admin;
  const [parties, setParties] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchList = useCallback(async () => {
    if (!user?.token) {
      return;
    }
    setLoading(true);
    try {
      const res = await api.specialShareParty.list.run({ token: user.token });
      if (res.result) {
        setParties(Array.isArray(res.data) ? res.data : []);
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      message.error(error.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    fetchList();
  }, [fetchList]);

  const totalPercent = parties
    .filter((item) => item.enabled)
    .reduce((sum, item) => sum + Number(item.percent_display || 0), 0);

  const propUserForSelector = useMemo(() => {
    if (!editing) {
      return false;
    }
    if (editing.user) {
      return {
        id: editing.user.id || editing.user_id,
        mobile: editing.user.mobile || editing.mobile,
        avatar: editing.user.avatar,
      };
    }
    if (editing.user_id) {
      return {
        id: editing.user_id,
        mobile: editing.mobile,
      };
    }
    return false;
  }, [editing]);

  const openCreate = () => {
    if (!canManage) {
      return;
    }
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({
      enabled: true,
      sort: 0,
      percent: 1,
    });
    setModalOpen(true);
  };

  const openEdit = (record) => {
    if (!canManage) {
      return;
    }
    setEditing(record);
    form.setFieldsValue({
      user_id: record.user_id,
      name: record.name,
      percent: record.percent_display,
      enabled: record.enabled,
      sort: record.sort,
    });
    setModalOpen(true);
  };

  const onSave = async (values) => {
    if (!user?.token || !canManage) {
      return;
    }
    setSaving(true);
    try {
      const payload = {
        user_id: values.user_id,
        name: values.name,
        percent: values.percent,
        enabled: values.enabled,
        sort: values.sort || 0,
      };
      const config = { token: user.token, data: payload };
      let res;
      if (editing) {
        res = await api.specialShareParty.update.run({
          ...config,
          path: { id: editing.id },
        });
      } else {
        res = await api.specialShareParty.create.run(config);
      }
      if (res.result) {
        message.success(res.message);
        setModalOpen(false);
        setEditing(null);
        fetchList();
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      message.error(error.message);
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async (id) => {
    if (!user?.token || !canManage) {
      return;
    }
    try {
      const res = await api.specialShareParty.delete.run({
        token: user.token,
        path: { id },
      });
      if (res.result) {
        message.success(res.message);
        fetchList();
      } else {
        message.warning(res.message);
      }
    } catch (error) {
      message.error(error.message);
    }
  };

  const columns = [
    {
      title: i18n.t("users.avatar"),
      key: "avatar",
      render: (_, record) => (
        <Avatar src={record.user?.avatar} alt={record.user?.nickname} />
      ),
    },
    {
      title: i18n.t("users.name"),
      key: "nickname",
      render: (_, record) => record.user?.nickname || record.name || "—",
    },
    {
      title: i18n.t("commissionList.mobile"),
      dataIndex: "mobile",
    },
    {
      title: i18n.t("specialShareParties.userId"),
      dataIndex: "user_id",
    },
    {
      title: i18n.t("specialShareParties.percent"),
      key: "percent",
      render: (_, record) => `${record.percent_display}%`,
    },
    {
      title: i18n.t("transferAccountList.priority"),
      dataIndex: "sort",
    },
    {
      title: i18n.t("transferAccountList.status"),
      key: "enabled",
      render: (_, record) =>
        record.enabled ? (
          <Tag color="green">{i18n.t("transferAccountList.enabled")}</Tag>
        ) : (
          <Tag>{i18n.t("transferAccountList.disabled")}</Tag>
        ),
    },
  ];

  if (canManage) {
    columns.push({
      title: i18n.t("users.actions"),
      key: "actions",
      render: (_, record) => (
        <Space>
          <Button type="link" onClick={() => openEdit(record)}>
            {i18n.t("transferAccountList.edit")}
          </Button>
          <Popconfirm
            title={i18n.t("specialShareParties.deleteConfirm")}
            onConfirm={() => onDelete(record.id)}
          >
            <Button type="link" danger>
              {i18n.t("transferAccountList.delete")}
            </Button>
          </Popconfirm>
        </Space>
      ),
    });
  }

  return (
    <>
      {!canManage ? (
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
          message={i18n.t("specialShareParties.viewOnlyNotice")}
        />
      ) : null}
      {totalPercent > 30 ? (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message={i18n.t("specialShareParties.totalPercentWarning").replace(
            "{{total}}",
            totalPercent
          )}
        />
      ) : null}
      <div style={{ marginBottom: 16 }}>
        {canManage ? (
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
            {i18n.t("specialShareParties.add")}
          </Button>
        ) : null}
        <span style={{ marginLeft: canManage ? 16 : 0, color: "#666" }}>
          {i18n.t("specialShareParties.totalPercent").replace("{{total}}", totalPercent)}
        </span>
      </div>
      <Table
        loading={loading}
        dataSource={parties}
        rowKey="id"
        pagination={false}
        columns={columns}
      />
      {canManage ? (
        <Modal
          key={editing ? `party-${editing.id}` : "party-new"}
          title={
            editing
              ? i18n.t("specialShareParties.editTitle")
              : i18n.t("specialShareParties.addTitle")
          }
          open={modalOpen}
          onCancel={() => {
            setModalOpen(false);
            setEditing(null);
          }}
          onOk={() => form.submit()}
          confirmLoading={saving}
          destroyOnClose
        >
          <Form form={form} layout="vertical" onFinish={onSave}>
            <Form.Item noStyle shouldUpdate>
              {({ setFieldsValue }) => (
                <Form.Item
                  name="user_id"
                  label={i18n.t("specialShareParties.selectUser")}
                  rules={[
                    {
                      required: true,
                      message: i18n.t("specialShareParties.userRequired"),
                    },
                  ]}
                >
                  <UserSelector
                    propUser={propUserForSelector}
                    setFieldsValue={setFieldsValue}
                  />
                </Form.Item>
              )}
            </Form.Item>
            <Form.Item name="name" label={i18n.t("specialShareParties.displayName")}>
              <Input placeholder={i18n.t("specialShareParties.displayNamePlaceholder")} />
            </Form.Item>
            <Form.Item
              name="percent"
              label={i18n.t("specialShareParties.percent")}
              rules={[
                { required: true, message: i18n.t("specialShareParties.percentRequired") },
              ]}
              extra={i18n.t("specialShareParties.percentHint")}
            >
              <InputNumber
                min={0.01}
                max={30}
                step={0.1}
                precision={2}
                style={{ width: "100%" }}
              />
            </Form.Item>
            <Form.Item name="sort" label={i18n.t("transferAccountList.priority")}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item
              name="enabled"
              label={i18n.t("transferAccountList.enabled")}
              valuePropName="checked"
            >
              <Switch />
            </Form.Item>
          </Form>
        </Modal>
      ) : null}
    </>
  );
}

export default connect((state) => ({
  user: getUserInfo(state),
}))(SpecialShareParties);
